import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const TenantContext = createContext(null);

// Use the configured backend URL - this is critical for subdomain routing
// The backend URL should be the main API server, not the subdomain
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Known platform domains that should NOT trigger tenant lookup
const PLATFORM_DOMAINS = [
  'fusionbuilderpro.com',
  'www.fusionbuilderpro.com',
  'app.fusionbuilderpro.com',
  'localhost',
  'preview.emergentagent.com'
];

// Default platform branding
const DEFAULT_BRANDING = {
  logo_url: null,
  logo_link_url: '/',
  favicon_url: null,
  pwa_icon_url: null,
  primary_color: '#2563EB',
  secondary_color: '#F97316',
  page_title: 'Fusion Builder Pro',
  page_description: 'Building Configurator'
};

/**
 * Check if the current hostname is a custom domain (not a platform domain)
 * Examples:
 *   builder.kayjaxsteel.com -> true (custom domain)
 *   kayjax-steel.fusionbuilderpro.com -> false (platform subdomain)
 *   fusionbuilderpro.com -> false (main platform)
 */
export const isCustomDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  
  // Check if it's a known platform domain or subdomain
  for (const platformDomain of PLATFORM_DOMAINS) {
    if (hostname === platformDomain || hostname.endsWith('.' + platformDomain)) {
      return false;
    }
  }
  
  // Check for preview/localhost
  if (hostname === 'localhost' || hostname.includes('.preview.emergentagent.com')) {
    return false;
  }
  
  // It's a custom domain if it's not any of the above
  return hostname.includes('.');
};

/**
 * Extract subdomain from current hostname (for platform subdomains)
 * Examples:
 *   kayjax-steel.fusionbuilderpro.com -> kayjax-steel
 *   demo.fusionbuilderpro.com -> demo
 *   fusionbuilderpro.com -> null (main platform)
 *   localhost:3000 -> null
 *   builderpro-2.preview.emergentagent.com -> null (preview env)
 */
export const extractSubdomain = () => {
  const hostname = window.location.hostname;
  
  console.log('[TenantContext] Checking hostname:', hostname);
  
  // Skip localhost and IP addresses
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    console.log('[TenantContext] Skipping - localhost or IP');
    return null;
  }
  
  // Skip preview environments (*.preview.emergentagent.com)
  if (hostname.includes('.preview.emergentagent.com')) {
    console.log('[TenantContext] Skipping - preview environment');
    return null;
  }
  
  const parts = hostname.split('.');
  console.log('[TenantContext] Hostname parts:', parts);
  
  // Need at least 3 parts for subdomain (sub.domain.tld)
  // e.g., kayjax-steel.fusionbuilderpro.com
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip common non-tenant subdomains
    if (['www', 'app', 'api', 'admin', 'staging', 'dev'].includes(subdomain)) {
      console.log('[TenantContext] Skipping - reserved subdomain:', subdomain);
      return null;
    }
    console.log('[TenantContext] Detected tenant subdomain:', subdomain);
    return subdomain;
  }
  
  console.log('[TenantContext] No subdomain detected');
  return null;
};

/**
 * Check URL params for tenant override (useful for testing)
 * Example: /demo?tenant=kayjax-steel
 */
export const getTenantFromParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('tenant');
};

/**
 * Check URL path for tenant slug
 * Example: /c/kayjax-steel -> kayjax-steel
 */
export const getTenantFromPath = () => {
  const path = window.location.pathname;
  const match = path.match(/^\/c\/([^/]+)/);
  return match ? match[1] : null;
};

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const initTenant = async () => {
      const hostname = window.location.hostname;
      
      // Priority 1: Check URL path (cleanest approach)
      // Example: /c/kayjax-steel
      let tenantSlug = getTenantFromPath();
      
      if (tenantSlug) {
        console.log('[TenantContext] Using tenant from URL path:', tenantSlug);
        await loadTenantBySlug(tenantSlug);
        return;
      }
      
      // Priority 2: Check URL params (for backwards compatibility)
      // Example: /demo?tenant=kayjax-steel
      tenantSlug = getTenantFromParams();
      
      if (tenantSlug) {
        console.log('[TenantContext] Using tenant from URL param:', tenantSlug);
        await loadTenantBySlug(tenantSlug);
        return;
      }
      
      // Priority 3: Check if this is a custom domain (e.g., builder.kayjaxsteel.com)
      // Note: Requires server wildcard domain support
      if (isCustomDomain()) {
        console.log('[TenantContext] Detected custom domain:', hostname);
        await loadTenantByCustomDomain(hostname);
        return;
      }
      
      // Priority 3: Check for platform subdomain (e.g., kayjax-steel.fusionbuilderpro.com)
      const subdomain = extractSubdomain();
      if (subdomain) {
        console.log('[TenantContext] Using tenant from subdomain:', subdomain);
        await loadTenantBySlug(subdomain);
        return;
      }
      
      // No tenant context - use platform defaults
      console.log('[TenantContext] No tenant detected, using platform defaults');
      setLoading(false);
    };
    
    const loadTenantBySlug = async (slug) => {
      try {
        console.log('[TenantContext] Loading tenant by slug:', slug);
        const response = await axios.get(`${API_URL}/api/settings/subdomain/${slug}`);
        applyTenantData(response.data);
      } catch (err) {
        handleTenantError(err, `slug: ${slug}`);
      }
    };
    
    const loadTenantByCustomDomain = async (domain) => {
      try {
        console.log('[TenantContext] Loading tenant by custom domain:', domain);
        const response = await axios.get(`${API_URL}/api/settings/domain/${encodeURIComponent(domain)}`);
        applyTenantData(response.data);
      } catch (err) {
        handleTenantError(err, `domain: ${domain}`);
      }
    };
    
    const applyTenantData = (tenantData) => {
      console.log('[TenantContext] Tenant loaded:', tenantData.name);
      
      setTenant(tenantData);
      setBranding({
        ...DEFAULT_BRANDING,
        ...tenantData.branding
      });
      
      // Update page title and favicon
      if (tenantData.branding?.page_title) {
        document.title = tenantData.branding.page_title;
      }
      if (tenantData.branding?.favicon_url) {
        updateFavicon(tenantData.branding.favicon_url);
      }
      
      setLoading(false);
    };
    
    const handleTenantError = (err, context) => {
      console.error(`[TenantContext] Failed to load tenant (${context}):`, err);
      console.error('[TenantContext] Error details:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Failed to load company');
      setLoading(false);
    };
    
    initTenant();
  }, []);
  
  // Helper to update favicon dynamically
  const updateFavicon = (url) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  };
  
  // Check if we're on a tenant subdomain
  const isTenantContext = () => {
    return tenant !== null;
  };
  
  // Get CSS variables for tenant branding
  const getCSSVariables = () => {
    return {
      '--tenant-primary': branding.primary_color,
      '--tenant-secondary': branding.secondary_color,
    };
  };
  
  const value = {
    tenant,
    branding,
    loading,
    error,
    isTenantContext,
    getCSSVariables,
    // Expose detection info for debugging
    currentSubdomain: extractSubdomain(),
    tenantParam: getTenantFromParams(),
    tenantPath: getTenantFromPath(),
    isCustomDomain: isCustomDomain()
  };
  
  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
